/**
 * Katalog-Vorschläge-Sidebar (LV-Katalog Phase 2 — 2026-05-07).
 *
 * Pro LvPosition rechts neben dem Bezeichnungs-/Langtext-Feld eine
 * collapsible Liste der Top-K LV-Katalog-Treffer aus dem dreiplus-Korpus
 * (oder Heinze-Korpus, je nach Backend-DB).
 *
 * Datenfluss:
 *   1. Bei `code` (Bauteil-Code) → Pre-Mapping aus `/api/lv-katalog/code-mappings/:bidiCode`
 *   2. Sonst Fallback Live-Lookup auf `/api/lv-katalog/lookup` mit gewerk + einheit
 *   3. Anzeige Top-5 mit Übernehmen-Button
 *   4. Übernehmen → onApply mit Kurztext, Langtext, ozKatalog
 *
 * Styling: konsistent mit CollapsibleSection (Atelier-Look) aus dem
 * Inspector-Refactor 2026-05-06.
 */
import { useEffect, useState } from "react";
import { api } from "../api";
import { CollapsibleSection } from "./CollapsibleSection";

interface Vorschlag {
  ozKatalog: string;
  hauptkategorie: { code: string; name: string };
  kurztext: string;
  langtextSnippet: string;
  einheit: string;
  confidence: number;
  reasoning: string;
}

export interface KatalogVorschlaegeSidebarProps {
  /** Bidi-Bauteil-Code (z.B. "IW01"). Wenn vorhanden → Pre-Mapping wird geladen. */
  bidiCode?: string | null;
  /** Fallback-Gewerk wenn kein Code (z.B. "rohbau"). */
  gewerk?: string;
  /** Optional: Einheit-Filter für Live-Lookup. */
  einheit?: string;
  /** Optional: Initiale dickeMm für die Query. */
  dickeMm?: number;
  /** Title-Text. Default „Katalog-Vorschläge". */
  title?: string;
  /** Default zugeklappt. */
  defaultOpen?: boolean;
  /** Callback bei „Übernehmen"-Klick. */
  onApply: (treffer: {
    ozKatalog: string;
    kurztext: string;
    langtext: string;
    einheit: string;
    katalogVersion: string;
  }) => void;
}

export function KatalogVorschlaegeSidebar({
  bidiCode,
  gewerk,
  einheit,
  dickeMm,
  title = "Katalog-Vorschläge",
  defaultOpen = false,
  onApply,
}: KatalogVorschlaegeSidebarProps) {
  const [vorschlaege, setVorschlaege] = useState<Vorschlag[]>([]);
  const [korpusVersion, setKorpusVersion] = useState<string>("");
  const [coverage, setCoverage] = useState<"good" | "weak" | "none" | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFreitextSearch, setShowFreitextSearch] = useState(false);
  const [freitext, setFreitext] = useState("");
  const [applyingOz, setApplyingOz] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        if (bidiCode) {
          // Pre-Mapping
          const res = await api.lvKatalogCodeMapping(bidiCode);
          if (cancelled) return;
          setVorschlaege(res.mapping.vorschlaege);
          setCoverage(res.mapping.coverage);
          setKorpusVersion(res.korpusVersion);
        } else if (gewerk) {
          // Live-Lookup
          const res = await api.lvKatalogLookup({
            gewerk,
            einheit,
            dickeMm,
            topK: 5,
          });
          if (cancelled) return;
          setVorschlaege(
            res.treffer.map((t) => ({
              ozKatalog: t.position.ozKatalog,
              hauptkategorie: t.position.hauptkategorie,
              kurztext: t.position.kurztext,
              langtextSnippet: t.position.langtext.slice(0, 200),
              einheit: t.position.einheit,
              confidence: t.confidence,
              reasoning: t.reasoning,
            })),
          );
          setCoverage(null);
          setKorpusVersion("");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [bidiCode, gewerk, einheit, dickeMm]);

  async function runFreitextSearch() {
    if (freitext.trim().length < 2 || !gewerk) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.lvKatalogLookup({
        gewerk,
        freitext: freitext.trim(),
        einheit,
        topK: 5,
      });
      setVorschlaege(
        res.treffer.map((t) => ({
          ozKatalog: t.position.ozKatalog,
          hauptkategorie: t.position.hauptkategorie,
          kurztext: t.position.kurztext,
          langtextSnippet: t.position.langtext.slice(0, 200),
          einheit: t.position.einheit,
          confidence: t.confidence,
          reasoning: t.reasoning,
        })),
      );
      setCoverage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleApply(v: Vorschlag) {
    setApplyingOz(v.ozKatalog);
    try {
      // Volltext für Übernahme nachladen (Snippet ist gekürzt)
      const detail = await api.lvKatalogPosition(v.ozKatalog);
      onApply({
        ozKatalog: v.ozKatalog,
        kurztext: v.kurztext,
        langtext: detail.langtext,
        einheit: v.einheit,
        katalogVersion: korpusVersion || "live-lookup",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setApplyingOz(null);
    }
  }

  const counter = vorschlaege.length;

  return (
    <CollapsibleSection
      icon="📚"
      title={title}
      counter={counter}
      defaultOpen={defaultOpen}
      hint="LV-Katalog-Vorschläge aus externem Korpus (z.B. dreiplus-demo)"
    >
      <div style={{ paddingTop: 8 }}>
        {loading && (
          <p className="text-[11px] text-slate-500 italic">Lädt…</p>
        )}
        {error && (
          <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-1.5">
            ⚠ {error}
          </p>
        )}
        {coverage && (
          <div
            className={`text-[10px] mb-2 inline-block px-1.5 py-0.5 rounded border font-mono ${
              coverage === "good"
                ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                : coverage === "weak"
                  ? "bg-amber-50 border-amber-300 text-amber-800"
                  : "bg-slate-50 border-slate-300 text-slate-600"
            }`}
            title="Coverage des Pre-Mappings: good = top-1 confidence ≥ 0.4, weak = ≥ 0.2, none = darunter"
          >
            coverage: {coverage}
            {korpusVersion ? ` · ${korpusVersion}` : ""}
          </div>
        )}
        {/* Freitext-Suche Toggle */}
        <div className="mb-2 flex items-center gap-2">
          <button
            onClick={() => setShowFreitextSearch((s) => !s)}
            className="text-[10px] px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded"
            title="Eigene Stichwortsuche im Korpus"
          >
            🔍 {showFreitextSearch ? "Suche schließen" : "Stichwort suchen"}
          </button>
        </div>
        {showFreitextSearch && (
          <div className="mb-2 flex items-center gap-1">
            <input
              type="text"
              value={freitext}
              onChange={(e) => setFreitext(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void runFreitextSearch();
                }
              }}
              placeholder="z.B. Innenputz Q2"
              className="flex-1 text-[11px] border border-slate-300 rounded px-1.5 py-0.5"
            />
            <button
              onClick={() => void runFreitextSearch()}
              disabled={freitext.trim().length < 2}
              className="text-[10px] px-1.5 py-0.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded"
            >
              Suchen
            </button>
          </div>
        )}
        {/* Treffer-Liste */}
        {!loading && vorschlaege.length === 0 && !error && (
          <p className="text-[11px] text-slate-400 italic">
            Keine Vorschläge — versuche eine Freitext-Suche oder prüfe das
            Bulk-Mapping (`pnpm --filter @bidi-takeoff/lv-katalog run bulk:map`).
          </p>
        )}
        <ul className="space-y-1.5">
          {vorschlaege.map((v) => {
            const confLabel =
              v.confidence >= 0.4 ? "good" : v.confidence >= 0.2 ? "weak" : "low";
            const confColor =
              v.confidence >= 0.4
                ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                : v.confidence >= 0.2
                  ? "bg-amber-100 text-amber-800 border-amber-300"
                  : "bg-slate-100 text-slate-600 border-slate-300";
            return (
              <li
                key={v.ozKatalog}
                className="border border-slate-200 rounded p-2 bg-white hover:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono text-slate-500 mb-0.5">
                      {v.ozKatalog} ·{" "}
                      <span title={v.hauptkategorie.name}>
                        HK {v.hauptkategorie.code}
                      </span>{" "}
                      · {v.einheit}
                    </div>
                    <div className="text-[12px] font-medium text-slate-900 truncate">
                      {v.kurztext}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${confColor}`}
                    title={`Reasoning: ${v.reasoning}`}
                  >
                    {confLabel} {v.confidence.toFixed(2)}
                  </span>
                </div>
                {v.langtextSnippet && (
                  <p className="text-[11px] text-slate-600 mb-1 line-clamp-3">
                    {v.langtextSnippet}…
                  </p>
                )}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => void handleApply(v)}
                    disabled={applyingOz === v.ozKatalog}
                    className="text-[10px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded font-medium"
                    title="Kurztext + Langtext in die LV-Position übernehmen"
                  >
                    {applyingOz === v.ozKatalog ? "Lädt…" : "✓ Übernehmen"}
                  </button>
                  <a
                    href={`/api/lv-katalog/positions/${encodeURIComponent(v.ozKatalog)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] px-2 py-0.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded"
                    title="Position-Detail im Backend ansehen (Volltext + Felder)"
                  >
                    Detail
                  </a>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </CollapsibleSection>
  );
}
